import React, { useState, useRef, useCallback } from 'react';
import VideoJS from './VideoJS';

/**
 * MediaCarousel — Instagram-style unified carousel for images + videos
 * Merges images[] and videos[] into a single swipeable set of slides.
 */
const MediaCarousel = ({ images = [], videos = [], onImageClick }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const videoRefs = useRef({});
    // Arrow visibility on hover is handled by CSS (.media-carousel:hover) so
    // mouse movement doesn't trigger a React re-render — re-rendering passes
    // a fresh `options` object literal to <VideoJS>, whose effect treats it
    // as a new source and reloads the player, which restarted playback.

    // Merge images and videos into unified media array
    const mediaItems = [
        ...images.map((img) => ({
            type: 'image',
            src: img.image,
        })),
        ...videos.map((vid) => ({
            type: vid.video_status && vid.video_status !== 'ready' ? 'video_processing' : 'video',
            src: vid.video_stream,
            thumbnail: vid.thumbnail,
            video_status: vid.video_status,
            aspect_ratio: vid.aspect_ratio,
        })),
    ];

    const totalSlides = mediaItems.length;

    const goToSlide = useCallback((index) => {
        if (index < 0 || index >= totalSlides) return;
        setActiveIndex(index);
    }, [totalSlides]);

    const goNext = useCallback((e) => {
        e.stopPropagation();
        goToSlide(activeIndex + 1);
    }, [activeIndex, goToSlide]);

    const goPrev = useCallback((e) => {
        e.stopPropagation();
        goToSlide(activeIndex - 1);
    }, [activeIndex, goToSlide]);

    // Don't render carousel wrapper if no media
    if (totalSlides === 0) return null;

    // Single item — no carousel controls needed
    if (totalSlides === 1) {
        const item = mediaItems[0];
        return (
            <div className="media-carousel">
                <div className="media-carousel-slides">
                    <div className="media-carousel-slide active">
                        {item.type === 'image' ? (
                            <img
                                src={item.src}
                                alt=""
                                className="media-carousel-image"
                                onClick={() => onImageClick && onImageClick(0)}
                            />
                        ) : item.type === 'video_processing' ? (
                            <div className="media-carousel-processing" style={{
                                background: item.thumbnail
                                    ? `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${item.thumbnail}) center/cover`
                                    : 'linear-gradient(135deg, #1a1a2e, #16213e)',
                            }}>
                                {item.video_status === 'failed' ? (
                                    <i className="fa-solid fa-circle-exclamation" style={{ fontSize: 36, color: '#ef4444' }}></i>
                                ) : (
                                    <div className="spinner-border text-light" role="status" style={{ width: 36, height: 36 }}>
                                        <span className="visually-hidden">Processing...</span>
                                    </div>
                                )}
                                <div className="mt-2" style={{ fontSize: 14, fontWeight: 500, color: 'white' }}>
                                    {item.video_status === 'failed' ? 'Video processing failed' : 'Your video is being processed...'}
                                </div>
                            </div>
                        ) : (
                            <VideoJS
                                options={{
                                    controls: true,
                                    preload: 'metadata',
                                    fluid: true,
                                    aspectRatio: '16:9',
                                    poster: item.thumbnail,
                                    sources: [{ src: item.src, type: 'video/mp4' }]
                                }}
                                enableScrollPause={true}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Multi-item carousel
    return (
        <div className="media-carousel">
            {/* Slide counter */}
            <div className="media-carousel-counter">
                {activeIndex + 1}/{totalSlides}
            </div>

            {/* Slides container */}
            <div className="media-carousel-track">
                <div
                    className="media-carousel-slides"
                    style={{ transform: `translateX(-${activeIndex * 100}%)` }}
                >
                    {mediaItems.map((item, i) => (
                        <div key={i} className={`media-carousel-slide ${i === activeIndex ? 'active' : ''}`}>
                            {item.type === 'image' ? (
                                <img
                                    src={item.src}
                                    alt=""
                                    className="media-carousel-image"
                                    onClick={() => onImageClick && onImageClick(i)}
                                />
                            ) : item.type === 'video_processing' ? (
                                <div className="media-carousel-processing" style={{
                                    background: item.thumbnail
                                        ? `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${item.thumbnail}) center/cover`
                                        : 'linear-gradient(135deg, #1a1a2e, #16213e)',
                                }}>
                                    {item.video_status === 'failed' ? (
                                        <i className="fa-solid fa-circle-exclamation" style={{ fontSize: 36, color: '#ef4444' }}></i>
                                    ) : (
                                        <div className="spinner-border text-light" role="status" style={{ width: 36, height: 36 }}>
                                            <span className="visually-hidden">Processing...</span>
                                        </div>
                                    )}
                                    <div className="mt-2" style={{ fontSize: 14, fontWeight: 500, color: 'white' }}>
                                        {item.video_status === 'failed' ? 'Video processing failed' : 'Your video is being processed...'}
                                    </div>
                                </div>
                            ) : (
                                <VideoJS
                                    options={{
                                        controls: true,
                                        preload: 'metadata',
                                        fluid: true,
                                        aspectRatio: '16:9',
                                        poster: item.thumbnail,
                                        sources: [{ src: item.src, type: 'video/mp4' }]
                                    }}
                                    enableScrollPause={i === activeIndex}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Left arrow */}
            {activeIndex > 0 && (
                <button
                    className="media-carousel-arrow media-carousel-arrow-left"
                    onClick={goPrev}
                    aria-label="Previous"
                >
                    ‹
                </button>
            )}

            {/* Right arrow */}
            {activeIndex < totalSlides - 1 && (
                <button
                    className="media-carousel-arrow media-carousel-arrow-right"
                    onClick={goNext}
                    aria-label="Next"
                >
                    ›
                </button>
            )}

            {/* Dot indicators */}
            <div className="media-carousel-dots">
                {mediaItems.map((_, i) => (
                    <button
                        key={i}
                        className={`media-carousel-dot ${i === activeIndex ? 'active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); goToSlide(i); }}
                        aria-label={`Go to slide ${i + 1}`}
                    />
                ))}
            </div>
        </div>
    );
};

export default MediaCarousel;
